import React, {useMemo} from 'react';
import {useParams} from 'react-router-dom';
import {twMerge} from 'tailwind-merge';

import {useIsSingleRun} from '../../../../pages/Run/RunWorkspaceRunContext';
import {DrawerContentType} from '../../WorkspaceDrawerContextProvider';
import {SettingToggleRow} from '../components/SettingToggleRow';
import {LinePlotCoordinateZooming} from './LinePlotCoordinateZooming';
import {useLinePlotSettingsContext} from './LinePlotSettingsContextProvider';
import {LinePlotTooltipNumRuns} from './LinePlotTooltipNumRuns';
import {getLinePlotSettingsWithDefaults} from './utils/getLinePlotSettings';

export const LinePlotDisplayPreferencesTab = ({
  displayedContentType,
}: {
  displayedContentType: DrawerContentType;
}) => {
  const {entityName} = useParams<{entityName: string}>();
  const isSingleRun = useIsSingleRun();

  const {
    clearSetting,
    displayLinePlotSettings,
    linePlotSettings,
    settingLevel,
    settingOverrideCounts,
    trackLinePlotSetting,
    updateLinePlotSettings,
  } = useLinePlotSettingsContext();
  const {
    colorRunNames,
    displayFullRunName,
    highlightedCompanionRunOnly,
    showLegend,
  } = useMemo(
    () => getLinePlotSettingsWithDefaults(displayLinePlotSettings),
    [displayLinePlotSettings]
  );

  return (
    <div
      data-test="workspace-line-plot-display-preferences"
      className={twMerge('flex flex-col', 'gap-20 py-16', 'h-full w-full')}>
      {/* We hide this setting on run page because there's only 1 run there. There's no need for this there. */}
      {!isSingleRun && (
        <SettingToggleRow
          clearSetting={clearSetting}
          displayValue={showLegend}
          emptySettings={{showLegend: undefined}}
          label="Display legends in all panels"
          numOverrides={settingOverrideCounts}
          onCheckedChange={(newValue: boolean) => {
            updateLinePlotSettings({showLegend: newValue});
            trackLinePlotSetting('showLegend', newValue.toString());
          }}
          settingKey="showLegend"
          settingLevel={settingLevel}
          value={linePlotSettings?.showLegend}
        />
      )}
      <SettingToggleRow
        clearSetting={clearSetting}
        displayValue={colorRunNames}
        emptySettings={{colorRunNames: undefined}}
        label="Display colored run names in tooltips"
        numOverrides={settingOverrideCounts}
        onCheckedChange={(newValue: boolean) => {
          updateLinePlotSettings({colorRunNames: newValue});
          trackLinePlotSetting('colorRunNames', newValue.toString());
        }}
        settingKey="colorRunNames"
        settingLevel={settingLevel}
        value={linePlotSettings?.colorRunNames}
      />
      <SettingToggleRow
        clearSetting={clearSetting}
        displayValue={highlightedCompanionRunOnly}
        emptySettings={{highlightedCompanionRunOnly: undefined}}
        label="Only show highlighted run in companion chart tooltips"
        numOverrides={settingOverrideCounts}
        onCheckedChange={(newValue: boolean) => {
          updateLinePlotSettings({
            highlightedCompanionRunOnly: newValue,
          });
          trackLinePlotSetting(
            'highlightedCompanionRunOnly',
            newValue.toString()
          );
        }}
        settingKey="highlightedCompanionRunOnly"
        settingLevel={settingLevel}
        value={linePlotSettings?.highlightedCompanionRunOnly}
      />
      <LinePlotTooltipNumRuns />
      <SettingToggleRow
        clearSetting={clearSetting}
        displayValue={displayFullRunName}
        emptySettings={{displayFullRunName: undefined}}
        label="Display full run names on the primary chart tooltip"
        numOverrides={settingOverrideCounts}
        onCheckedChange={(newValue: boolean) => {
          updateLinePlotSettings({
            displayFullRunName: newValue,
          });
          trackLinePlotSetting('displayFullRunName', newValue.toString());
        }}
        settingKey="displayFullRunName"
        settingLevel={settingLevel}
        value={linePlotSettings?.displayFullRunName}
      />
      {displayedContentType === 'workspace-settings' && (
        <LinePlotCoordinateZooming
          coordinateZooming={
            displayLinePlotSettings?.coordinateZooming ?? false
          }
          trackSetting={trackLinePlotSetting}
          updateSetting={updateLinePlotSettings}
        />
      )}
    </div>
  );
};
